import { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import apiUrls from "../../../api";
import { buildGraph } from "../../graph/util/graphBuilder";
import type { Content } from "../../graph/util/dataTypes/Content";
import type { ContentType } from "../../graph/util/dataTypes/ContentType";
import type { Action } from "../../graph/util/dataTypes/Action";
import type { FlatGraphResponse } from "../../graph/util/dataTypes/FlatGraphResponse";
import type {
  StatsNode,
  StatsNodeLink,
  GraphStatsData,
  GraphLayout,
  InteractionStatData,
  NodeStats,
} from "../types/graphStatsTypes";
import type { SessionSubdata, GroupedInteraction } from "../types/sessionTypes";

// Asset type detection (same logic as SVGGraph.tsx)
type AssetType = "image" | "video" | "location" | "sound" | "control" | "model3d" | "effect";

const detectAssetType = (modelName: string): AssetType => {
  switch (modelName) {
    case "content-audio":
    case "content-avatar-audio-player":
      return "sound";
    case "content-thumbnail":
    case "content-onboarding":
    case "content-photo-catch":
    case "content-pdf-viewer":
      return "image";
    case "content-hotspot-scene-video":
    case "content-hotspot-scene-image":
    case "content-vr-locator":
    case "content-zone-detect":
      return "location";
    case "content-video-2-d":
      return "video";
    case "content-3d-viewer":
      return "model3d";
    case "content-fire":
    case "content-blur":
    case "content-dust":
      return "effect";
    default:
      return "control";
  }
};

interface UseGraphStatsOptions {
  projectId: number;
  startDate?: string;
  endDate?: string;
}

interface UseGraphStatsResult extends GraphStatsData {
  refetch: () => void;
}

/**
 * Hook to fetch and merge graph structure with stats data for visualization
 */
export const useGraphStats = ({
  projectId,
  startDate,
  endDate,
}: UseGraphStatsOptions): UseGraphStatsResult => {
  const [nodes, setNodes] = useState<StatsNode[]>([]);
  const [links, setLinks] = useState<StatsNodeLink[]>([]);
  const [layout, setLayout] = useState<GraphLayout>({ width: 0, height: 0, x: 0, y: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();

  const fetchKeyRef = useRef<string | null>(null);
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  const refetch = () => setRefetchTrigger((prev) => prev + 1);

  useEffect(() => {
    const fetchGraphAndStats = async () => {
      const fetchKey = `${projectId}-${startDate || "null"}-${endDate || "null"}-${refetchTrigger}`;
      if (fetchKeyRef.current === fetchKey) return;

      // Wait for auth token to be available (race condition on page refresh)
      const maxWaitTime = 5000; // 5 seconds max
      const checkInterval = 100; // Check every 100ms
      let waited = 0;
      while (!axios.defaults.headers.common["Authorization"] && waited < maxWaitTime) {
        await new Promise((resolve) => setTimeout(resolve, checkInterval));
        waited += checkInterval;
      }

      // If still no token after waiting, don't make the request
      if (!axios.defaults.headers.common["Authorization"]) {
        setIsLoading(false);
        setError("Authentication not ready");
        return;
      }

      fetchKeyRef.current = fetchKey;

      setIsLoading(true);
      setError(undefined);

      try {
        // Fetch graph structure and interaction stats in parallel
        const [graphResponse, statsResponse] = await Promise.all([
          axios.get<FlatGraphResponse>(
            `${apiUrls.projects.graph.flat}/${projectId}?isForGraph=true`,
          ),
          axios.get(
            `${apiUrls.stats.interactionStats}?project=${projectId}${
              startDate ? `&startDate=${startDate}` : ""
            }${endDate ? `&endDate=${endDate}` : ""}`,
          ),
        ]);

        const graphData = graphResponse.data;
        const interactionStats: InteractionStatData[] = statsResponse.data || [];

        // Build the graph using the existing graphBuilder
        const contents: Content[] = graphData.contents || [];
        const contentTypes: ContentType[] = graphData.contentTypes || [];
        const actions: Action[] = graphData.actions || [];

        // For stats, show all assets including those in branching/menu scenes
        const [graphNodes, rootLayout, graphLinks] = buildGraph(contents, contentTypes, actions, {
          showAllAssets: true,
        });

        // Create a map of interaction stats by contentId
        const statsMap = new Map<number, InteractionStatData>();
        interactionStats.forEach((stat) => {
          statsMap.set(stat.contentId, stat);
        });

        // Calculate scene stats from interactions inside each scene
        // Group interactions by sceneId and calculate weighted average success rate
        const sceneStatsMap = new Map<
          number,
          {
            totalWeightedSuccess: number;
            totalAttempts: number;
            totalDuration: number;
            count: number;
          }
        >();
        interactionStats.forEach((stat) => {
          if (stat.sceneId != null) {
            const existing = sceneStatsMap.get(stat.sceneId) || {
              totalWeightedSuccess: 0,
              totalAttempts: 0,
              totalDuration: 0,
              count: 0,
            };
            // Weight success rate by attempt count for more accurate average
            existing.totalWeightedSuccess += stat.successRate * stat.attemptCount;
            existing.totalAttempts += stat.attemptCount;
            existing.totalDuration += stat.avgDuration * stat.attemptCount;
            existing.count += 1;
            sceneStatsMap.set(stat.sceneId, existing);
          }
        });

        // Transform graph nodes to StatsNodes with merged stats
        const statsNodes: StatsNode[] = graphNodes.map((node) => {
          const stat = statsMap.get(node.contentId);

          const statsNode: StatsNode = {
            contentId: node.contentId,
            contentTypeId: node.contentTypeId,
            name: node.details?.name || "",
            type: node.type === "scene" ? "scene" : "asset",
            modelName: node.modelName,
            x: node.x,
            y: node.y,
          };

          if (node.type === "scene") {
            // Scene-specific properties
            statsNode.isStartScene = node.details?.is_start_scene || false;
            statsNode.sceneType = node.details?.scene_type;

            // Attach aggregated scene stats from child interactions
            const sceneStats = sceneStatsMap.get(node.contentId);
            if (sceneStats && sceneStats.totalAttempts > 0) {
              statsNode.stats = {
                successRate: sceneStats.totalWeightedSuccess / sceneStats.totalAttempts,
                attemptCount: sceneStats.totalAttempts,
                avgDuration: sceneStats.totalDuration / sceneStats.totalAttempts,
              };
            }
          } else {
            // Asset-specific properties
            statsNode.assetType = detectAssetType(node.modelName);
            // Check if this interaction is scored (has be_scored: true in details)
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            statsNode.isScored = (node.details as any)?.be_scored === true;

            // Attach stats if available
            if (stat) {
              statsNode.stats = {
                successRate: stat.successRate,
                attemptCount: stat.attemptCount,
                avgDuration: stat.avgDuration,
                totalScore: stat.avgScore,
                totalScoreMax: undefined, // Not provided by API
              };
            }
          }

          return statsNode;
        });

        // Create a map of StatsNodes by contentId for link resolution
        const nodesMap = new Map<number, StatsNode>();
        statsNodes.forEach((node) => {
          nodesMap.set(node.contentId, node);
        });

        // Transform graph links to StatsNodeLinks
        const statsLinks: StatsNodeLink[] = graphLinks
          .map((link): StatsNodeLink | null => {
            const fromNode = nodesMap.get(link.from.contentId);
            const toNode = nodesMap.get(link.to.contentId);

            if (!fromNode || !toNode) return null;

            return {
              from: fromNode,
              to: toNode,
              linksToScene: link.linksToScene,
            };
          })
          .filter((link): link is StatsNodeLink => link !== null);

        setNodes(statsNodes);
        setLinks(statsLinks);
        setLayout({
          width: rootLayout.width,
          height: rootLayout.height,
          x: rootLayout.x,
          y: rootLayout.y,
        });
      } catch (err) {
        console.error("Error fetching graph stats:", err);
        setError(err instanceof Error ? err.message : "Failed to load graph data");
        setNodes([]);
        setLinks([]);
        setLayout({ width: 0, height: 0, x: 0, y: 0 });
      } finally {
        setIsLoading(false);
      }
    };

    if (projectId) {
      fetchGraphAndStats();
    }
  }, [projectId, startDate, endDate, refetchTrigger]);

  return {
    nodes,
    links,
    layout,
    isLoading,
    error,
    refetch,
  };
};

/**
 * Hook to create graph visualization from session subdata (individual session view)
 * This overlays visited/not-visited status on the graph nodes
 */
interface UseSessionGraphStatsOptions {
  projectId: number;
  sessionSubdata: SessionSubdata[];
  groupedInteractions: GroupedInteraction[];
  /** All visited content IDs including scenes and non-scored interactions */
  allVisitedContentIds?: Set<number>;
}

interface UseSessionGraphStatsResult extends GraphStatsData {
  refetch: () => void;
}

// Store raw graph data to avoid race conditions
interface RawGraphData {
  graphNodes: ReturnType<typeof buildGraph>[0];
  rootLayout: ReturnType<typeof buildGraph>[1];
  graphLinks: ReturnType<typeof buildGraph>[2];
}

export const useSessionGraphStats = ({
  projectId,
  sessionSubdata,
  groupedInteractions,
  allVisitedContentIds: externalVisitedContentIds,
}: UseSessionGraphStatsOptions): UseSessionGraphStatsResult => {
  // Store raw graph data separately from transformed nodes
  const [rawGraphData, setRawGraphData] = useState<RawGraphData | null>(null);
  const [layout, setLayout] = useState<GraphLayout>({ width: 0, height: 0, x: 0, y: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();

  const fetchKeyRef = useRef<string | null>(null);
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  const refetch = () => setRefetchTrigger((prev) => prev + 1);

  // Create a set of ALL visited content IDs (any content with time_start_content > 0)
  // Use external set if provided (includes scenes and non-scored interactions)
  // Otherwise fall back to deriving from sessionSubdata (only scored interactions)
  const visitedContentIds = useMemo(() => {
    // If external visited IDs are provided, use them (they include all visited content)
    if (externalVisitedContentIds && externalVisitedContentIds.size > 0) {
      return externalVisitedContentIds;
    }
    // Fallback: derive from sessionSubdata (only scored interactions)
    const ids = new Set<number>();
    sessionSubdata.forEach((item) => {
      if (item.content?.id && item.time_start_content > 0) {
        ids.add(item.content.id);
        if (item.content.parentId) {
          ids.add(item.content.parentId);
        }
      }
    });
    return ids;
  }, [sessionSubdata, externalVisitedContentIds]);

  // Create a map of interaction stats from groupedInteractions
  const interactionStats = useMemo(() => {
    const map = new Map<number, NodeStats>();

    groupedInteractions.forEach((group) => {
      const contentId = group.latest.content?.id;
      if (!contentId) return;

      const score = group.latest.score;
      const scoreMax = group.latest.scoreMax || 1;
      const successRate = Math.round((score / scoreMax) * 100);

      map.set(contentId, {
        successRate,
        attemptCount: group.attemptCount,
        avgDuration: group.latest.time_end_content - group.latest.time_start_content,
        totalScore: score,
        totalScoreMax: scoreMax,
      });
    });

    return map;
  }, [groupedInteractions]);

  // Calculate scene stats from interactions inside each scene (weighted average)
  const sceneStats = useMemo(() => {
    const map = new Map<number, NodeStats>();

    // Group interactions by parent scene
    const sceneInteractions = new Map<
      number,
      {
        totalWeightedSuccess: number;
        totalAttempts: number;
        totalDuration: number;
        count: number;
        totalScore: number;
        totalScoreMax: number;
      }
    >();

    groupedInteractions.forEach((group) => {
      const parentId = group.latest.content?.parentId;
      if (!parentId) return;

      const score = group.latest.score;
      const scoreMax = group.latest.scoreMax || 1;
      const successRate = Math.round((score / scoreMax) * 100);

      const existing = sceneInteractions.get(parentId) || {
        totalWeightedSuccess: 0,
        totalAttempts: 0,
        totalDuration: 0,
        count: 0,
        totalScore: 0,
        totalScoreMax: 0,
      };

      existing.totalWeightedSuccess += successRate * group.attemptCount;
      existing.totalAttempts += group.attemptCount;
      existing.totalDuration +=
        (group.latest.time_end_content - group.latest.time_start_content) * group.attemptCount;
      existing.count += 1;
      existing.totalScore += score;
      existing.totalScoreMax += scoreMax;

      sceneInteractions.set(parentId, existing);
    });

    // Convert to NodeStats
    sceneInteractions.forEach((data, sceneId) => {
      if (data.totalAttempts > 0) {
        map.set(sceneId, {
          successRate: data.totalWeightedSuccess / data.totalAttempts,
          attemptCount: data.totalAttempts,
          avgDuration: data.totalDuration / data.totalAttempts,
          totalScore: data.totalScore,
          totalScoreMax: data.totalScoreMax,
        });
      }
    });

    return map;
  }, [groupedInteractions]);

  // Fetch graph data ONLY when projectId changes (not when session data changes)
  useEffect(() => {
    const fetchGraph = async () => {
      const fetchKey = `session-${projectId}-${refetchTrigger}`;
      if (fetchKeyRef.current === fetchKey) return;

      // Wait for auth token to be available (race condition on page refresh)
      const maxWaitTime = 5000; // 5 seconds max
      const checkInterval = 100; // Check every 100ms
      let waited = 0;
      while (!axios.defaults.headers.common["Authorization"] && waited < maxWaitTime) {
        await new Promise((resolve) => setTimeout(resolve, checkInterval));
        waited += checkInterval;
      }

      // If still no token after waiting, don't make the request
      if (!axios.defaults.headers.common["Authorization"]) {
        setIsLoading(false);
        setError("Authentication not ready");
        return;
      }

      fetchKeyRef.current = fetchKey;

      setIsLoading(true);
      setError(undefined);

      try {
        const graphResponse = await axios.get<FlatGraphResponse>(
          `${apiUrls.projects.graph.flat}/${projectId}?isForGraph=true`,
        );

        const graphData = graphResponse.data;

        // Build the graph using the existing graphBuilder
        const contents: Content[] = graphData.contents || [];
        const contentTypes: ContentType[] = graphData.contentTypes || [];
        const actions: Action[] = graphData.actions || [];

        // For stats, show all assets including those in branching/menu scenes
        const [graphNodes, rootLayout, graphLinks] = buildGraph(contents, contentTypes, actions, {
          showAllAssets: true,
        });

        // Store raw graph data
        setRawGraphData({ graphNodes, rootLayout, graphLinks });
        setLayout({
          width: rootLayout.width,
          height: rootLayout.height,
          x: rootLayout.x,
          y: rootLayout.y,
        });
      } catch (err) {
        console.error("Error fetching session graph:", err);
        setError(err instanceof Error ? err.message : "Failed to load graph data");
        setRawGraphData(null);
        setLayout({ width: 0, height: 0, x: 0, y: 0 });
      } finally {
        setIsLoading(false);
      }
    };

    if (projectId) {
      fetchGraph();
    }
  }, [projectId, refetchTrigger]);

  // Transform nodes synchronously whenever raw data or session data changes
  // This avoids race conditions from multiple async operations
  const { nodes, links } = useMemo(() => {
    if (!rawGraphData) {
      return { nodes: [], links: [] };
    }

    const { graphNodes, graphLinks } = rawGraphData;

    // Transform graph nodes to StatsNodes with visited/not-visited status
    const statsNodes: StatsNode[] = graphNodes.map((node) => {
      // A node is visited if it has time_start_content > 0 in sessionSubdata
      const isVisited = visitedContentIds.has(node.contentId);

      const statsNode: StatsNode = {
        contentId: node.contentId,
        contentTypeId: node.contentTypeId,
        name: node.details?.name || "",
        type: node.type === "scene" ? "scene" : "asset",
        modelName: node.modelName,
        x: node.x,
        y: node.y,
        visited: isVisited,
      };

      if (node.type === "scene") {
        statsNode.isStartScene = node.details?.is_start_scene || false;
        statsNode.sceneType = node.details?.scene_type;
        // Attach aggregated scene stats from child interactions (weighted average)
        const sceneStat = sceneStats.get(node.contentId);
        if (sceneStat) {
          statsNode.stats = sceneStat;
        }
      } else {
        statsNode.assetType = detectAssetType(node.modelName);
        // Check if this interaction is scored (has be_scored: true in details)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        statsNode.isScored = (node.details as any)?.be_scored === true;
        // Attach stats from session interaction data
        const interactionStat = interactionStats.get(node.contentId);
        if (interactionStat) {
          statsNode.stats = interactionStat;
        }
      }

      return statsNode;
    });

    // Create a map of StatsNodes by contentId for link resolution
    const nodesMap = new Map<number, StatsNode>();
    statsNodes.forEach((node) => {
      nodesMap.set(node.contentId, node);
    });

    // Transform graph links to StatsNodeLinks
    const statsLinks: StatsNodeLink[] = graphLinks
      .map((link): StatsNodeLink | null => {
        const fromNode = nodesMap.get(link.from.contentId);
        const toNode = nodesMap.get(link.to.contentId);

        if (!fromNode || !toNode) return null;

        return {
          from: fromNode,
          to: toNode,
          linksToScene: link.linksToScene,
        };
      })
      .filter((link): link is StatsNodeLink => link !== null);

    return { nodes: statsNodes, links: statsLinks };
  }, [rawGraphData, visitedContentIds, interactionStats, sceneStats]);

  return {
    nodes,
    links,
    layout,
    isLoading,
    error,
    refetch,
  };
};

export default useGraphStats;
